// Screenshots and screen recordings on support requests and their replies.
// One list, read by the portal (a fast, friendly "no") and the API (the only opinion that counts —
// it also sniffs magic bytes, and the sniffed type, never the browser's, decides what it serves).
// Images and video only: every type here is safe to render inline. No SVG (a script container),
// HTML, PDF or archives.

export const SUPPORT_MAX_FILES = 6;
/** Per file — comfortably fits a minute of screen recording. */
export const SUPPORT_MAX_FILE_BYTES = 25 * 1024 * 1024;
/** Across one report or reply. */
export const SUPPORT_MAX_TOTAL_BYTES = 50 * 1024 * 1024;

export const SUPPORT_FILE_TYPES = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
} as const;
export type SupportFileMime = (typeof SUPPORT_FILE_TYPES)[keyof typeof SUPPORT_FILE_TYPES];

export const SUPPORT_ACCEPT = Object.keys(SUPPORT_FILE_TYPES).map((ext) => `.${ext}`).join(",");
export const SUPPORT_ACCEPTED_SUMMARY = "PNG, JPG, GIF, WebP, MP4, MOV or WebM";

export const BUG_SEVERITIES = ["low", "normal", "high", "critical"] as const;
export type BugSeverity = (typeof BUG_SEVERITIES)[number];
export const BUG_SEVERITY_LABELS: Record<BugSeverity, string> = {
  low: "Low — a small annoyance",
  normal: "Normal — something's not right",
  high: "High — I can't finish what I'm doing",
  critical: "Critical — nothing works / I'm losing data",
};

/** Lowercase extension without the dot, or "". */
export function supportExtension(name: string): string {
  const i = name.lastIndexOf(".");
  return i < 0 || i === name.length - 1 ? "" : name.slice(i + 1).toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function isSupportExtension(ext: string): ext is keyof typeof SUPPORT_FILE_TYPES {
  return Object.prototype.hasOwnProperty.call(SUPPORT_FILE_TYPES, ext);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** An attachment as the API returns it. Read it from `supportAttachmentUrl`. */
export interface SupportAttachment {
  id: string;
  request_id: string;
  comment_id: string | null;
  name: string;
  mime: string;
  size: number;
  created_at: number;
}

export function supportAttachmentUrl(a: Pick<SupportAttachment, "id" | "request_id">): string {
  return `/api/support/${encodeURIComponent(a.request_id)}/attachments/${encodeURIComponent(a.id)}`;
}
